import { useState, useContext, useEffect } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';


async function addYearToDatabase(info) {
    return genericPOSTDatabaseCall('/api/cfi/addEntityYear', info, 'entity year');
}

async function deleteYearInDatabase(year) {
    return genericDELETEDatabaseCall('/api/cfi/deleteEntityYear/', { year }, 'entity year');
}

async function saveYearsInDatabase(years) {
    return genericPOSTDatabaseCall('/api/cfi/updateEntityYears', { years }, 'entity years');
}

function RenderYears({ years, setCompanySelected, messageApi }) {
    // Group years into chunks of 4
    const chunkedYears = [];
    for (let i = 0; i < years.length; i += 4) {
        chunkedYears.push(years.slice(i, i + 4));
    }

    const handleInputChange = (event, yearIndex, field) => {
        const newValue = event.target.value;
        setCompanySelected(prev => {
            const updatedYears = [...prev.years];
            updatedYears[yearIndex] = {
                ...updatedYears[yearIndex],
                [field]: newValue
            };
            return {
                ...prev,
                years: updatedYears
            };
        });
    }

    const handleInputBlur = (event, yearIndex, field) => {
        var newValue = event.target.value;
        // Update the state with the new value for the specific year
        const regex = /^(\d{0,13}(\.\d{0,2})?)?$/;

        if (!regex.test(newValue)) {
            messageApi.error('Invalid input. Please enter a valid number. Max 13 digits before decimal and 2 after.');
            newValue = '0.00'; // Default value if invalid
        }
        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = '0.00'; // Default value if empty
        }

        setCompanySelected(prev => {
            const updatedYears = [...prev.years];
            updatedYears[yearIndex] = {
                ...updatedYears[yearIndex],
                [field]: newValue
            };
            return {
                ...prev,
                years: updatedYears
            };
        });
    }

    const handleDeleteYear = async (deleteYear) => {
        messageApi.loading('Deleting year...');
        try {
            await deleteYearInDatabase(deleteYear);
            setCompanySelected(prev => {
                const updatedYears = prev.years.filter(year => year.id !== deleteYear.id);
                return {
                    ...prev,
                    years: updatedYears
                };
            });
            messageApi.destroy();
            messageApi.success('Year deleted successfully');
        } catch (error) {
            messageApi.destroy();
            messageApi.error('Failed to delete year');
        }
    }

    return (
        <div>
            {chunkedYears.map((yearChunk, chunkIndex) => (
                <table className="entity-table">
                    <thead>
                        <tr>
                            <th></th>
                            {yearChunk.map((yearData, index) => (
                                <th key={`year-${index}`}>{"Year " + (index + chunkIndex * 4)} <DeleteOutlined onClick={() => handleDeleteYear(yearData)} style={{ marginLeft: '10px', color: 'red', cursor: 'pointer', fontSize: '16px' }} /></th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Operating revenue</td>
                            {yearChunk.map((yearData, yearIndex) => (
                                <td key={`rev-${yearIndex}`}>
                                    <input
                                        type="text"
                                        className="form-input editable-cell"
                                        value={yearData.revenue}
                                        onChange={(e) => handleInputChange(e, yearIndex + chunkIndex * 4, 'revenue')}
                                        onBlur={(e) => handleInputBlur(e, yearIndex + chunkIndex * 4, 'revenue')}
                                    />
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td>Operating expenses</td>
                            {yearChunk.map((yearData, yearIndex) => (
                                <td key={`exp-${yearIndex}`}>
                                    <input
                                        type="text"
                                        className="form-input editable-cell"
                                        value={yearData.expenses}
                                        onChange={(e) => handleInputChange(e, yearIndex + chunkIndex * 4, 'expenses')}
                                        onBlur={(e) => handleInputBlur(e, yearIndex + chunkIndex * 4, 'expenses')}
                                    />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            ))}
        </div>
    );
}

function ProjectionsPage() {
    const { messageApi, entitySelected, setEntitySelected, setPageNum } = useContext(CriticalFinancePageContext);
    const [nextDisabled, setNextDisabled] = useState(entitySelected.years.length === 0 || entitySelected.years.length !== (parseInt(entitySelected.years_to_ipo) + 1));

    useEffect(() => {
        setNextDisabled(entitySelected.years.length === 0 || entitySelected.years.length !== (parseInt(entitySelected.years_to_ipo) + 1));
    }, [entitySelected?.years.length, entitySelected?.years_to_ipo]);

    const handleAddYear = async () => {
        messageApi.loading('Adding year...');
        try {
            const newYear = await addYearToDatabase({ entityId: entitySelected.id });
            setEntitySelected(prev => ({
                ...prev,
                years: [...prev.years, newYear]
            }));
            messageApi.destroy();
            messageApi.success('Year added successfully');
        } catch (error) {
            messageApi.destroy();
            messageApi.error('Failed to add year');
        }
    }

    const saveAllYears = async () => {
        messageApi.loading('Saving all years...');
        try {
            // Implement save logic here
            await saveYearsInDatabase(entitySelected.years);
            messageApi.destroy();
            messageApi.success('All years saved successfully');
        } catch (error) {
            messageApi.destroy();
            messageApi.error('Failed to save years');
        }
    }

    return (
        <div className="financial-builder-container entity-industry-container">
            <h2>Financial Projection (Years until IPO: {entitySelected.years_to_ipo})</h2>
            <div>
                <RenderYears years={entitySelected.years} setCompanySelected={setEntitySelected} messageApi={messageApi} />
            </div>
            <div className="form-section">
                <button onClick={handleAddYear} className="add-button">ADD Year</button>
                <button onClick={saveAllYears} className="save-button">SAVE All Years</button>
            </div>
            <div className="navigation-button-container">
                <button type="button" className="next-button" onClick={() => setPageNum(1)}>Back</button>
                <button type="button" className={nextDisabled ? "next-button disabled" : "next-button"} onClick={() => {
                    if (!nextDisabled) {
                        setPageNum(3);
                    } else {
                        messageApi.error('Please ensure the number of years added matches the years to IPO before proceeding and it is not zero.');
                    }
                }}>Next</button>
            </div>
        </div>
    );
}


export default ProjectionsPage;